import { cn } from '@invent/wl-ui-kit-next';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@invent/wl-ui-kit-next/ui';
import { ArrowDown, ArrowUp, Check, ChevronDown, Eye, Link2, List, Lock, Pin, Plus, Users } from 'lucide-react';
import * as React from 'react';

import {
  DataTable,
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  StatusChip
} from '@/design';
import { useAuth } from '@/domains/auth';
import { formatDateTime } from '@/domains/survey/format';
import type { IEtagged } from '@/domains/api';
import { useCan } from '@/domains/auth';
import type { ISavedView, ISavedViewSummary, SavedViewVisibility } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { useAdminData } from '../../components/admin-page';
import { RenameDialog, storedBody, viewRefusal, type IViewProblem } from './save-view';
import { MIGRATED_MARK, SEEDED_MARK, listSurveyLabel, visibilityLabel } from '../../lists';

/**
 * The view picker (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §3.3) — Conga's, made live over `GET /views?survey=&kind=list`.
 *
 * **A popover**: *Recently opened* — the views the caller has opened, newest first — then every
 * other view; a search box narrows both by name and description. **survey-api orders the list**
 * (opened first, newest first, then by name), and the popover keeps its order. Each row says its
 * name, description, visibility and whose it is: the owner's email, or **Seeded**. *Manage views…*
 * opens "Select a view": the same rows in a table, a radio per row, *Open* — in survey-api's order
 * until a column's header sorts it.
 *
 * **The list is read when the picker opens**, not once with the page: opening a view stamps its
 * `openedAt`, so a list read before is already out of order by the time anyone looks at it.
 *
 * The current view carries `*` before its name while the screen differs from it — *Changed — not
 * saved* (§3.3). Its writes are *Save view ▾*'s (`save-view.tsx`), beside this — but for *Delete* in
 * "Select a view", offered on a row the caller `mayEdit` and never on a seed (confirmed). The
 * picker's row carries no revision, so the delete reads the view first for the `ETag` it quotes.
 */

export function viewOwner(view: Pick<ISavedViewSummary, 'seed' | 'owner'>): string {
  if (view.seed !== null) return SEEDED_MARK;
  return view.owner?.email ?? 'an account since removed';
}

/** Who sees a view, at a glance (SR-412): a lock for a private one, people for a shared one, an eye
 *  for a shared, read-only one — beside its name in the list and on the picker once chosen. */
const VISIBILITY_ICONS: Record<SavedViewVisibility, typeof Lock> = {
  private: Lock,
  shared: Users,
  shared_read_only: Eye
};

function VisibilityIcon({ visibility, className }: { visibility: SavedViewVisibility; className?: string }) {
  const Icon = VISIBILITY_ICONS[visibility] ?? Users;
  return <Icon className={cn('size-3.5 shrink-0 text-muted-foreground', className)} aria-label={visibilityLabel(visibility)} role="img" />;
}

function describe(view: ISavedViewSummary): string {
  return [
    view.description,
    visibilityLabel(view.visibility),
    viewOwner(view),
    view.pinned ? 'Pinned conditions' : null,
    view.migrated ? MIGRATED_MARK : null
  ]
    .filter(Boolean)
    .join(' · ');
}

function matches(view: ISavedViewSummary, needle: string): boolean {
  return (
    needle === '' ||
    view.name.toLowerCase().includes(needle) ||
    (view.description ?? '').toLowerCase().includes(needle)
  );
}

export function ViewPicker({
  survey,
  kind = 'list',
  current,
  changed,
  onOpen,
  onDeleted,
  onNew,
  onSaved,
  unsavedName,
  buttonMaxWidth
}: {
  /** The button's widest, where what sits beside it varies (the header's tags). */
  buttonMaxWidth?: string;
  /** A view the dialog renamed or made the default (SR-412) — the screen's own, where it is the one on
   *  screen, takes it in place. */
  onSaved?: (view: IEtagged<ISavedView>) => void;
  survey: string;
  kind?: 'list' | 'report';
  /** The view on screen — its id, name and who sees it — or null where the survey has none yet. */
  current: Nullable<{ id: string; name: string; visibility?: SavedViewVisibility }>;
  changed: boolean;
  onOpen: (viewId: string) => void;
  /** A view deleted from "Select a view" — the screen's own, where it was the one on screen. */
  onDeleted: (viewId: string) => void;
  /** *New list* (SR-412): an unfiltered list, the basic columns, no view — built, then *Save as…*. */
  onNew?: () => void;
  /** What the picker says with no view on screen, where that is not "Select a view". */
  unsavedName?: string;
}) {
  const [open, setOpen] = React.useState(false);
  const [managing, setManaging] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const asking = open || managing;
  const { data, error, reload } = useAdminData(
    (signal) => (asking ? listsApi.listViews(survey, kind, signal) : Promise.resolve(null)),
    [survey, kind, asking]
  );
  const needle = search.trim().toLowerCase();
  const views = (data ?? []).filter((view) => matches(view, needle));
  const recent = views.filter((view) => view.openedAt !== null);
  const others = views.filter((view) => view.openedAt === null);
  const pick = (viewId: string) => {
    setOpen(false);
    setSearch('');
    onOpen(viewId);
  };

  // A survey with no view opened and no default: the picker asks for one.
  const name = current?.name ?? unsavedName ?? 'Select a view';
  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="outline"
            data-view={current?.id ?? ''}
            aria-label={`View: ${name}${changed ? ', changed and not saved' : ''}`}
            title={name}
            style={buttonMaxWidth ? { maxWidth: buttonMaxWidth } : undefined}
            className="h-8 min-w-0 max-w-[26rem] gap-1.5 font-normal"
          >
            {current?.visibility && <VisibilityIcon visibility={current.visibility} />}
            <span className="truncate">
              {changed && <span aria-hidden>*</span>}
              {name}
            </span>
            <ChevronDown className="size-4 shrink-0" aria-hidden />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          aria-label="Views"
          className="grid max-h-[min(32rem,var(--radix-popover-content-available-height))] w-[min(30rem,calc(100vw-2rem))] gap-2 overflow-y-auto p-3 text-[0.8125rem]"
        >
          <Input
            type="search"
            aria-label="Search the views"
            placeholder="Search views…"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="h-8"
          />
          {error ? (
            <p role="alert" className="text-destructive">
              The views could not be read: {error}
            </p>
          ) : data === null ? (
            <p className="text-muted-foreground">Reading the views…</p>
          ) : (
            <>
              <ViewGroup title="Recently opened" views={recent} current={current?.id} onPick={pick} />
              <ViewGroup
                title={recent.length > 0 ? 'Every other view' : 'Views'}
                views={others}
                current={current?.id}
                onPick={pick}
              />
              {views.length === 0 && (
                <p className="text-muted-foreground">
                  {needle === '' ? 'No view of this survey yet.' : 'No view matches.'}
                </p>
              )}
            </>
          )}
          {onNew && (
            <Button
              type="button"
              variant="ghost"
              className="h-8 justify-start gap-1.5 border-t border-border font-normal"
              onClick={() => {
                setOpen(false);
                setSearch('');
                onNew();
              }}
            >
              <Plus className="size-4" aria-hidden />
              New list
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            className={cn('h-8 justify-start gap-1.5 font-normal', !onNew && 'border-t border-border')}
            onClick={() => {
              setOpen(false);
              setManaging(true);
            }}
          >
            <List className="size-4" aria-hidden />
            Manage views…
          </Button>
        </PopoverContent>
      </Popover>
      <SelectAView
        survey={survey}
        onNew={onNew}
        onSaved={(view) => {
          void reload();
          onSaved?.(view);
        }}
        open={managing}
        onOpenChange={setManaging}
        views={data}
        error={error}
        current={current?.id ?? null}
        onOpen={onOpen}
        onDeleted={(viewId) => {
          void reload();
          onDeleted(viewId);
        }}
      />
    </>
  );
}

function ViewGroup({
  title,
  views,
  current,
  onPick
}: {
  title: string;
  views: readonly ISavedViewSummary[];
  current: string | undefined;
  onPick: (viewId: string) => void;
}) {
  if (views.length === 0) return null;
  return (
    <div className="grid gap-0.5">
      <p className="text-xs font-medium text-[var(--veritant-royal-70)]">{title}</p>
      <ul className="grid gap-0.5">
        {views.map((view) => (
          <li key={view.id}>
            <button
              type="button"
              aria-current={view.id === current ? 'true' : undefined}
              onClick={() => onPick(view.id)}
              className="flex w-full items-start gap-2 rounded px-2 py-1.5 text-left outline-none hover:bg-[var(--accent)] focus-visible:ring-2 focus-visible:ring-ring"
            >
              <Check
                className={cn('mt-0.5 size-4 shrink-0', view.id === current ? 'opacity-100' : 'opacity-0')}
                aria-hidden
              />
              <span className="grid min-w-0">
                <span className="flex items-center gap-1.5 font-medium">
                  <VisibilityIcon visibility={view.visibility} />
                  {view.name}
                </span>
                <span className="text-xs text-muted-foreground">{describe(view)}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** The dialog's own narrowing: every view, the reader's own, the shared ones, or the private ones. */
type ViewsShown = 'all' | 'mine' | 'shared' | 'private';
const VIEWS_SHOWN: { key: ViewsShown; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'mine', label: 'Mine' },
  { key: 'shared', label: 'Shared' },
  { key: 'private', label: 'Private' }
];

/** Conga's "Select a View": every view as a table, one chosen by its radio and opened. */
function SelectAView({
  open,
  onOpenChange,
  views,
  error,
  current,
  onOpen,
  onDeleted,
  onNew,
  onSaved,
  survey
}: {
  /** *New list*, from the dialog too (SR-412). */
  onNew?: () => void;
  /** A view renamed or made the default here. */
  onSaved: (view: IEtagged<ISavedView>) => void;
  survey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  views: Nullable<ISavedViewSummary[]>;
  error: Nullable<string>;
  current: Nullable<string>;
  onOpen: (viewId: string) => void;
  onDeleted: (viewId: string) => void;
}) {
  const [search, setSearch] = React.useState('');
  const [narrow, setNarrow] = React.useState<ViewsShown>('all');
  // The table's sort (SR-412): survey-api's order — opened first, newest open first — until a header
  // is pressed; a header's press sorts by it, again the other way, a third time back.
  const [sort, setSort] = React.useState<Nullable<{ by: SortBy; desc: boolean }>>(null);
  const [renaming, setRenaming] = React.useState<Nullable<IEtagged<ISavedView>>>(null);
  const [renameProblems, setRenameProblems] = React.useState<IViewProblem[]>([]);
  const canShare = useCan()('lists.share');
  const me = useAuth().user?.accountId ?? null;
  const [chosen, setChosen] = React.useState(current);
  const [confirming, setConfirming] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<{ text: string; alert: boolean }>>(null);
  const [wasOpen, setWasOpen] = React.useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setChosen(current);
      setSaid(null);
    }
  }
  // An Enter on a row opens it; a click only chooses it (`onOpen` is the table's answer to both).
  const byKey = React.useRef(false);
  // One open per dialog: a double-click's second press, or an Enter after it, finds it gone.
  const opening = React.useRef(false);
  React.useEffect(() => {
    if (open) opening.current = false;
  }, [open]);

  /** The view's revision is its read's `ETag` — the row carries none — then the delete under it. */
  const remove = async (view: ISavedViewSummary) => {
    setDeleting(true);
    setSaid(null);
    try {
      const read = await listsApi.peekView(view.id);
      if (read !== null) {
        if (read.etag === null) {
          setSaid({ text: 'This view came without its revision — it cannot be deleted safely.', alert: true });
          return;
        }
        await listsApi.deleteView(view.id, read.etag);
      }
      setChosen(null);
      setSaid({ text: `The view ${view.name} was deleted.`, alert: false });
      onDeleted(view.id);
    } catch (deleteError) {
      setSaid({
        text: viewRefusal(deleteError, 'the delete').said.text,
        alert: true
      });
    } finally {
      setDeleting(false);
    }
  };
  const needle = search.trim().toLowerCase();
  const kept = (view: ISavedViewSummary) =>
    narrow === 'all' ||
    (narrow === 'mine' && view.owner?.accountId === me && view.seed === null) ||
    (narrow === 'shared' && view.visibility !== 'private') ||
    (narrow === 'private' && view.visibility === 'private');
  const filtered = views?.filter((view) => matches(view, needle) && kept(view)) ?? null;
  const shown = filtered && sort ? [...filtered].sort((a, b) => compareViews(a, b, sort.by) * (sort.desc ? -1 : 1)) : filtered;
  // What Delete, Rename…, Make default and Open act on is a row on screen: a search or a tab that
  // hides the chosen view drops the choice.
  const chosenView = shown?.find((view) => view.id === chosen) ?? null;
  if (shown !== null && chosen !== null && chosenView === null) setChosen(null);
  const deletable = chosenView !== null && chosenView.mayEdit && chosenView.seed === null;
  const sortHeader = (by: SortBy, label: string) => {
    const on = sort?.by === by;
    const Arrow = on && sort?.desc ? ArrowDown : ArrowUp;
    return (
      <button
        type="button"
        aria-label={`${label}${on ? `, sorted ${sort?.desc ? 'descending' : 'ascending'}` : ''}`}
        onClick={() => setSort(!on ? { by, desc: by !== 'name' } : sort?.desc === (by !== 'name') ? { by, desc: !sort.desc } : null)}
        className="inline-flex items-center gap-1 rounded px-0.5 outline-none hover:text-[var(--color-royal-blue)] focus-visible:ring-2 focus-visible:ring-ring"
      >
        {label}
        <Arrow className={cn('size-3', on ? 'opacity-100' : 'opacity-0')} aria-hidden />
      </button>
    );
  };
  /** The chosen view read with its revision, unstamped, for a write from here — or said why not. */
  const peek = async (view: ISavedViewSummary): Promise<Nullable<IEtagged<ISavedView>>> => {
    const read = await listsApi.peekView(view.id);
    if (read === null) {
      setSaid({ text: `The view ${view.name} is no longer there.`, alert: true });
      return null;
    }
    if (read.etag === null) {
      setSaid({ text: 'This view came without its revision — it cannot be changed safely.', alert: true });
      return null;
    }
    return read;
  };
  const makeDefault = async (view: ISavedViewSummary) => {
    setSaid(null);
    try {
      const read = await peek(view);
      if (!read) return;
      const saved = await listsApi.saveView(view.id, read.etag!, { ...storedBody(read.body), isDefault: true });
      setSaid({ text: `${view.name} is the default for ${listSurveyLabel(survey)}.`, alert: false });
      onSaved(saved);
    } catch (error) {
      setSaid({ text: viewRefusal(error, 'making it the default').said.text, alert: true });
    }
  };
  const copyLink = async (view: ISavedViewSummary) => {
    const link = `${window.location.origin}/team/lists?${new URLSearchParams({ survey, view: view.id })}`;
    try {
      await navigator.clipboard.writeText(link);
      setSaid({ text: `The link to ${view.name} is copied.`, alert: false });
    } catch {
      setSaid({ text: `The link could not be copied: ${link}`, alert: true });
    }
  };
  const countOf = (key: ViewsShown) =>
    (views ?? []).filter(
      (view) =>
        key === 'all' ||
        (key === 'mine' && view.owner?.accountId === me && view.seed === null) ||
        (key === 'shared' && view.visibility !== 'private') ||
        (key === 'private' && view.visibility === 'private')
    ).length;
  const openNow = (viewId: string) => {
    if (opening.current) return;
    opening.current = true;
    onOpenChange(false);
    onOpen(viewId);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Select a view</DialogTitle>
          <DialogDescription>
            Every view of this survey you may open. Click a row to choose it, double-click to open it.
          </DialogDescription>
        </DialogHeader>
        <DialogBody className="gap-3">
          <div className="flex flex-wrap items-center gap-2">
            <Input
              type="search"
              aria-label="Search the views"
              placeholder="Search views by name or description…"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="h-8 min-w-48 flex-1"
            />
            <div role="radiogroup" aria-label="Show" className="flex items-center gap-1">
              {VIEWS_SHOWN.map(({ key, label }) => (
                <button
                  key={key}
                  type="button"
                  role="radio"
                  aria-checked={narrow === key}
                  onClick={() => setNarrow(key)}
                  className={cn(
                    'h-8 rounded-full border px-3 text-[0.8125rem] outline-none focus-visible:ring-2 focus-visible:ring-ring',
                    narrow === key
                      ? 'border-[var(--color-teal)] bg-[color-mix(in_srgb,var(--color-teal)_10%,white)] font-medium text-[var(--color-royal-blue)]'
                      : 'border-border bg-white text-muted-foreground hover:text-[var(--color-royal-blue)]'
                  )}
                >
                  {label} <span className="tabular-nums text-muted-foreground">{countOf(key)}</span>
                </button>
              ))}
            </div>
            {onNew && (
              <Button
                type="button"
                variant="outline"
                className="h-8 gap-1.5 font-normal"
                onClick={() => {
                  onOpenChange(false);
                  onNew();
                }}
              >
                <Plus className="size-4" aria-hidden />
                New list
              </Button>
            )}
          </div>
          <DataTable
            label="Views"
            maxHeight="26rem"
            rows={shown}
            rowKey={(view) => view.id}
            onOpen={(view) => {
              if (byKey.current) openNow(view.id);
              else setChosen(view.id);
              byKey.current = false;
            }}
            rowProps={(view) => ({
              onDoubleClick: () => openNow(view.id),
              onKeyDownCapture: (event) => {
                byKey.current = event.key === 'Enter' && event.target === event.currentTarget;
              }
            })}
            selected={chosen}
            state={
              error
                ? { kind: 'error', content: <p role="alert">The views could not be read: {error}</p> }
                : shown === null
                  ? { kind: 'empty', content: 'Reading the views…' }
                  : { kind: 'empty', content: needle === '' ? 'No view of this survey yet.' : 'No view matches.' }
            }
            columns={[
              {
                id: 'choose',
                header: <span className="sr-only">Choose</span>,
                label: 'Choose',
                className: 'w-8 pr-0',
                cell: (view) => (
                  <input
                    type="radio"
                    name="list-view"
                    aria-label={view.name}
                    checked={chosen === view.id}
                    onChange={() => setChosen(view.id)}
                    className="accent-[var(--color-teal)]"
                  />
                )
              },
              {
                id: 'name',
                header: sortHeader('name', 'Name'),
                label: 'Name',
                cell: (view) => (
                  <span className="grid gap-0.5">
                    <span className="flex flex-wrap items-center gap-1.5 font-medium">
                      <VisibilityIcon visibility={view.visibility} />
                      {view.name}
                      {view.id === current && <StatusChip tone="info">Open now</StatusChip>}
                      {view.isDefault && <StatusChip tone="success">Default</StatusChip>}
                      {view.seed !== null && <StatusChip>{SEEDED_MARK}</StatusChip>}
                      {view.pinned && (
                        <StatusChip tone="attention">
                          <span className="inline-flex items-center gap-1">
                            <Pin className="size-3" aria-hidden />
                            Pinned
                          </span>
                        </StatusChip>
                      )}
                      {view.migrated && <StatusChip>{MIGRATED_MARK}</StatusChip>}
                    </span>
                    {view.description && (
                      <span className="text-xs text-muted-foreground">{view.description}</span>
                    )}
                  </span>
                )
              },
              {
                id: 'visibility',
                header: 'Who sees it',
                cell: (view) => (
                  <span className="grid">
                    {visibilityLabel(view.visibility)}
                    <span className="text-xs text-muted-foreground">{viewOwner(view)}</span>
                  </span>
                )
              },
              {
                id: 'opened',
                header: sortHeader('opened', 'You opened'),
                label: 'You opened',
                className: 'whitespace-nowrap',
                cell: (view) => (view.openedAt ? formatDateTime(view.openedAt) : <span className="text-muted-foreground">—</span>)
              },
              {
                id: 'updated',
                header: sortHeader('updated', 'Changed'),
                label: 'Changed',
                className: 'whitespace-nowrap',
                cell: (view) => formatDateTime(view.updatedAt)
              }
            ]}
          />
        </DialogBody>
        {said && (
          <p role={said.alert ? 'alert' : 'status'} className={cn('px-6 text-sm', said.alert ? 'text-destructive' : 'text-muted-foreground')}>
            {said.text}
          </p>
        )}
        <DialogFooter>
          {chosenView !== null && (
            <Button
              type="button"
              variant="outline"
              className="mr-auto"
              disabled={!deletable || deleting}
              onClick={() => setConfirming(true)}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          {chosenView !== null && (
            <>
              <Button
                type="button"
                variant="outline"
                disabled={!chosenView.mayEdit}
                title={chosenView.mayEdit ? undefined : 'Only who may save over this view renames it'}
                onClick={async () => {
                  setSaid(null);
                  setRenameProblems([]);
                  const read = await peek(chosenView).catch((error) => {
                    setSaid({ text: viewRefusal(error, 'the read').said.text, alert: true });
                    return null;
                  });
                  if (read) setRenaming(read);
                }}
              >
                Rename…
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={!chosenView.mayEdit || !canShare || chosenView.visibility === 'private' || chosenView.isDefault}
                title={
                  chosenView.isDefault
                    ? `Already the default for ${listSurveyLabel(survey)}`
                    : chosenView.visibility === 'private'
                      ? 'Only a shared view can be the default'
                      : !canShare || !chosenView.mayEdit
                        ? 'Your Team Portal roles do not include sharing a view or making one the default'
                        : undefined
                }
                onClick={() => void makeDefault(chosenView)}
              >
                Make default
              </Button>
              <Button type="button" variant="outline" className="gap-1.5" onClick={() => void copyLink(chosenView)}>
                <Link2 className="size-4" aria-hidden />
                Copy link
              </Button>
            </>
          )}
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="button" disabled={chosenView === null} onClick={() => chosenView && openNow(chosenView.id)}>
            Open
          </Button>
        </DialogFooter>
        {chosenView !== null && !deletable && (
          <p className="px-6 pb-4 text-xs text-muted-foreground">
            {chosenView.seed !== null
              ? 'A seeded view is never deleted.'
              : 'Only who may save over a view deletes it.'}
          </p>
        )}
      </DialogContent>
      <AlertDialog open={confirming} onOpenChange={setConfirming}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete the view {chosenView?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {chosenView?.visibility === 'private'
                ? 'Nobody else sees it.'
                : 'Everyone it is shared with loses it too.'}{' '}
              Nothing undoes a delete.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirming(false);
                if (chosenView) void remove(chosenView);
              }}
            >
              Delete the view
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {renaming && (
        <RenameDialog
          open
          view={renaming.body}
          problems={renameProblems}
          onClose={() => setRenaming(null)}
          onRename={async (name, description) => {
            try {
              const saved = await listsApi.saveView(renaming.body.id, renaming.etag!, {
                ...storedBody(renaming.body),
                name,
                description
              });
              setRenaming(null);
              setSaid({ text: `Renamed to ${name}.`, alert: false });
              onSaved(saved);
            } catch (error) {
              const refusal = viewRefusal(error, 'the rename');
              setRenameProblems(refusal.problems);
              if (refusal.problems.length === 0) {
                setRenaming(null);
                setSaid({ text: refusal.said.text, alert: true });
              }
            }
          }}
        />
      )}
    </Dialog>
  );
}

/** What the dialog's table sorts by (SR-412). */
type SortBy = 'name' | 'opened' | 'updated';

/** Two views by `by`, ascending: names ignoring case, a never-opened view before any opened one. */
function compareViews(a: ISavedViewSummary, b: ISavedViewSummary, by: SortBy): number {
  if (by === 'name') return a.name.localeCompare(b.name, 'en', { sensitivity: 'base' });
  const at = (view: ISavedViewSummary) => Date.parse((by === 'opened' ? view.openedAt : view.updatedAt) ?? '') || 0;
  return at(a) - at(b);
}
